import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, X } from "lucide-react";
import {
  getMasterTerms, createMasterTerm, updateMasterTerm, deleteMasterTerm,
} from "../../api/api";
import DataTable from "../../components/DataTable";
import SegmentedControl from "../../components/SegmentedControl";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { CLINICAL_TERM_TYPE, CLINICAL_TERM_TYPE_LABEL } from "../../lib/prescription-constants";

const EMPTY = { name: "", type: CLINICAL_TERM_TYPE.DIAGNOSIS, category: "", active: true };

function TermForm({ open, onOpenChange, term, onSaved, defaultType }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!term;

  useEffect(() => {
    if (!open) return;
    setForm(term ? { ...EMPTY, ...term } : { ...EMPTY, type: defaultType });
  }, [open, term, defaultType]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        category: form.category.trim(),
        active: !!form.active,
      };
      if (editing) await updateMasterTerm(term._id, payload);
      else await createMasterTerm(payload);
      toast.success(editing ? "Term updated." : "Term added.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save the term.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit term" : "Add term"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="term-type">Type</Label>
            <select id="term-type" value={form.type} onChange={(e) => set("type", e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
              {Object.values(CLINICAL_TERM_TYPE).map((t) => (
                <option key={t} value={t}>{CLINICAL_TERM_TYPE_LABEL[t]}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="term-name">Name *</Label>
            <Input id="term-name" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Viral Fever" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="term-cat">Category (optional)</Label>
            <Input id="term-cat" value={form.category} onChange={(e) => set("category", e.target.value)}
              placeholder="General Medicine" />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.active} onCheckedChange={(v) => set("active", v)} />
            Available for prescribing
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add term"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ClinicalTermMaster() {
  const [type, setType] = useState(CLINICAL_TERM_TYPE.DIAGNOSIS);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getMasterTerms({ type, search: search || undefined, page, limit: 20 })
      .then((res) => {
        setTerms(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load the list."))
      .finally(() => setLoading(false));
  }, [type, search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (term) => {
    try {
      const res = await deleteMasterTerm(term._id);
      toast.success(res.message || "Term removed.");
      load();
    } catch (err) {
      toast.error(err?.message || "Could not remove the term.");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (t) => (
        <div className="min-w-0">
          <p className="truncate font-medium leading-tight">{t.name}</p>
          {t.category && <p className="truncate text-xs leading-tight text-muted-foreground">{t.category}</p>}
        </div>
      ),
    },
    {
      key: "active",
      header: "Status",
      render: (t) => (
        <Badge variant={t.active ? "secondary" : "outline"}>
          {t.active ? "Active" : "Retired"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8"
            aria-label={`Edit ${t.name}`}
            onClick={() => { setEditing(t); setFormOpen(true); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${t.name}`}
            onClick={() => remove(t)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Diagnoses & Symptoms</h2>
          <p className="text-sm text-muted-foreground">
            The vocabulary doctors pick from while writing a prescription
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="h-4 w-4" /> Add term
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          items={[
            { value: CLINICAL_TERM_TYPE.DIAGNOSIS, label: "Diagnoses" },
            { value: CLINICAL_TERM_TYPE.SYMPTOM, label: "Symptoms" },
          ]}
          value={type}
          onChange={(v) => { setType(v); setPage(1); }}
        />
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search…"
            className="h-9 pl-9 pr-9"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {!loading && (
        <p className="text-sm text-muted-foreground">
          {meta.total} {CLINICAL_TERM_TYPE_LABEL[type].toLowerCase()}{meta.total === 1 ? "" : "s"}
        </p>
      )}

      <DataTable
        columns={columns}
        data={terms}
        loading={loading}
        emptyMessage={search ? "Nothing matches that search." : "Nothing here yet."}
        emptyAction={
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" /> Add the first one
          </Button>
        }
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(t) => t._id}
      />

      <TermForm
        open={formOpen}
        onOpenChange={setFormOpen}
        term={editing}
        defaultType={type}
        onSaved={load}
      />
    </div>
  );
}
